import { useCallback, useContext, useState } from "react";
import toast from "react-hot-toast";
import { GlobalContext } from "../../../Context/GlobalContext";

export default function useInventory() {
  const { api, anlookApi } = useContext(GlobalContext);

  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  const getMasterInventory = useCallback(async () => {
    setLoading(true);

    try {
      const res = await api.post("/Inventory/getMasterInventoryV2");

      if (res?.data?.responseCode === "00") {
        return {
          success: true,
          data: Array.isArray(res?.data?.response) ? res.data.response : [],
          message: res?.data?.responseMessage || "Inventory fetched",
        };
      }

      return {
        success: false,
        data: [],
        message: res?.data?.responseMessage || "Failed to fetch inventory",
      };
    } catch (error) {
      console.error("getMasterInventoryV2 error:", error);

      return {
        success: false,
        data: [],
        message: "Something went wrong while fetching inventory",
      };
    } finally {
      setLoading(false);
    }
  }, [api]);

 const uploadInventoryImage = useCallback(
   async ({ imageData, itemName }) => {
     if (!imageData) return "";

     // Image pehle se uploaded URL hai
     if (!String(imageData).startsWith("data:")) {
       return imageData;
     }

     if (!anlookApi) {
       throw new Error("Anlook API instance missing");
     }

     try {
       const upload = await anlookApi.post("/v1/uploadinventoryimagesv2.php", {
         item_name: itemName || "inventory",
         type: "IMG",
         image_data: imageData,
       });

       if (String(upload?.data?.status) === "0" && upload?.data?.image_url) {
         return upload.data.image_url;
       }

       throw new Error(
         upload?.data?.message ||
           upload?.data?.msg ||
           upload?.data?.responseMessage ||
           "Image upload failed",
       );
     } catch (error) {
       console.error(
         "uploadInventoryImage error:",
         error?.response?.data || error,
       );

       throw new Error(
         error?.response?.data?.message ||
           error?.response?.data?.msg ||
           error?.response?.data?.responseMessage ||
           error?.message ||
           "Image upload failed",
       );
     }
   },
   [anlookApi],
 );

  const updateMasterInventory = useCallback(
    async ({ id, hsCode, imgUrl, itemName }) => {
      setUpdating(true);

      try {
        if (!id) {
          return {
            success: false,
            message: "Inventory id is required",
          };
        }

        let finalImageUrl = imgUrl || "";

        // Upload image first if image is base64
        if (finalImageUrl?.startsWith("data:")) {
          finalImageUrl = await uploadInventoryImage({
            imageData: finalImageUrl,
            itemName,
          });
        }

        const res = await api.post("/Inventory/updateMasterInventoryV2", {
          id: String(id),
          hsCode: String(hsCode || ""),
          imgUrl: String(finalImageUrl || ""),
        });

        const isSuccess =
          res?.data?.responseCode === "00" ||
          res?.data?.status === "0" ||
          res?.data?.status === "1";

        if (isSuccess) {
          return {
            success: true,
            data: res?.data,
            imageUrl: finalImageUrl,
            message:
              res?.data?.responseMessage ||
              res?.data?.msg ||
              "Inventory updated successfully",
          };
        }

        return {
          success: false,
          data: res?.data,
          imageUrl: finalImageUrl,
          message:
            res?.data?.responseMessage ||
            res?.data?.msg ||
            "Failed to update inventory",
        };
      } catch (error) {
        console.error("updateMasterInventoryV2 error:", error);

        return {
          success: false,
          message:
            error?.message || "Something went wrong while updating inventory",
        };
      } finally {
        setUpdating(false);
      }
    },
    [api, uploadInventoryImage]
  );

  const updateInventoryAndToast = useCallback(
    async (payload) => {
      const result = await updateMasterInventory(payload);

      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }

      return result;
    },
    [updateMasterInventory]
  );

  return {
    loading,
    updating,

    getMasterInventory,
    updateMasterInventory,
    updateInventoryAndToast,
    uploadInventoryImage,
  };
}